import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { StatusBadge } from '@/components/training/StatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  type EnrollmentAttendance,
  type TrainingEnrollmentListItem,
  useRecordAttendance,
  useTrainingAttendances,
  useTrainingEnrollments,
} from '@/hooks/use-training-enrollments'
import { useTraining } from '@/hooks/use-trainings'
import { useTenantPath } from '@/lib/tenant'

/** サーバーが出欠記録を受け付ける受講ステータス（それ以外は 409 になる） */
const RECORDABLE_STATUSES = ['confirmed', 'in_progress']

/** 一覧に出さない受講ステータス（却下・キャンセル） */
const HIDDEN_STATUSES = ['rejected', 'cancelled']

function attendanceKey(enrollmentId: string, scheduleId: string) {
  return `${enrollmentId}:${scheduleId}`
}

export default function AttendanceManagePage() {
  const { id } = useParams<{ id: string }>()
  const tp = useTenantPath()
  const { data: training, isLoading } = useTraining(id ?? '')
  const { data: enrollmentData, isLoading: isLoadingEnrollments } = useTrainingEnrollments(id ?? '')
  const { data: attendanceData } = useTrainingAttendances(id ?? '')
  const recordAttendance = useRecordAttendance()
  const [activeSchedule, setActiveSchedule] = useState<string>('')

  const t = training as
    | {
        title?: string
        schedules?: {
          id: string
          dayNumber: number
          scheduleDate: string
          location?: string | null
        }[]
      }
    | undefined
  const schedules = t?.schedules ?? []
  const enrollments = (enrollmentData ?? []).filter(
    (e: TrainingEnrollmentListItem) => !HIDDEN_STATUSES.includes(e.status),
  )

  // 「受講ID:日程ID」で出席状況を引けるようにする
  const attended = new Map(
    (attendanceData ?? []).map((a: EnrollmentAttendance) => [
      attendanceKey(a.enrollmentId, a.scheduleId),
      a.attended,
    ]),
  )

  const handleAttendance = async (enrollmentId: string, scheduleId: string, value: boolean) => {
    try {
      await recordAttendance.mutateAsync({ enrollmentId, data: { scheduleId, attended: value } })
      toast.success(value ? '出席を記録しました' : '欠席を記録しました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '出欠記録に失敗しました')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to={tp('/teacher/trainings')}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-1 h-4 w-4" />
            戻る
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">出欠管理</h1>
      </div>

      {t && <p className="text-muted-foreground">研修: {t.title}</p>}

      {schedules.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">日程が登録されていません</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={activeSchedule || schedules[0]?.id} onValueChange={setActiveSchedule}>
          <TabsList>
            {schedules.map((s) => (
              <TabsTrigger key={s.id} value={s.id}>
                Day {s.dayNumber}
              </TabsTrigger>
            ))}
          </TabsList>

          {schedules.map((s) => (
            <TabsContent key={s.id} value={s.id}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Day {s.dayNumber} - {new Date(s.scheduleDate).toLocaleString('ja-JP')}
                    {s.location && ` (${s.location})`}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingEnrollments ? (
                    <Skeleton className="h-24" />
                  ) : enrollments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      受講者がいません。受講者を割り当てると、ここで出欠を記録できます。
                    </p>
                  ) : (
                    <div className="divide-y">
                      {enrollments.map((e: TrainingEnrollmentListItem) => (
                        <div key={e.id} className="flex items-center justify-between gap-3 py-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">
                              {e.userName ?? '(名前未設定)'}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">{e.userEmail}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <StatusBadge type="enrollment" status={e.status} />
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={attended.get(attendanceKey(e.id, s.id)) ?? false}
                                // 承認待ちや受講後のステータスでは記録できない（サーバーが 409 を返す）
                                disabled={
                                  recordAttendance.isPending ||
                                  !RECORDABLE_STATUSES.includes(e.status)
                                }
                                onCheckedChange={(value) => handleAttendance(e.id, s.id, value)}
                                aria-label={`${e.userName ?? e.id} の出欠`}
                              />
                              <span className="w-8 text-sm text-muted-foreground">
                                {attended.get(attendanceKey(e.id, s.id)) ? '出席' : '欠席'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  )
}
